import { DeleteOutlined } from '@mui/icons-material';
import { MenuItem, ListItemIcon, ListItemText } from '@mui/material';

import { use } from 'react';

import { graphql, type DocumentType } from '@/gql';
import { useMutation } from '@/hooks/mutationWithSnackbar';

import { PINNED_POSTS_COLLECTION_QUERY } from '@/app/(main)/g/[machineName]/noticeboard/_components/noticeboard/PinnedPostsCollection';

import { SERVICE_GROUPS_PINNED_POSTS_QUERY } from '@/app/(main)/s/[machineName]/admin/noticeboards/posts/ServiceMultiGroupPinnedPostsAccordion';

import { ServiceAdminMultiGroupContext } from '@/app/(main)/s/[machineName]/admin/contexts/ServiceAdminMultiGroupContext';

import { SERVICE_GROUPS_POSTS_QUERY } from '@/app/(main)/s/[machineName]/admin/noticeboards/posts/ServiceMultiGroupPostsEntry';

import { USERS_GROUPS_POSTS_QUERY } from '@/app/(main)/myHub/home/_components/MultiGroupPostsEntry';

import { MultiGroupContext } from '@/app/(main)/myHub/contexts/MultiGroupContext';

import { CurrentUserContext } from '@/context/CurrentUserContext';

import { POST_COLLECTION_QUERY, POST_LIMIT } from '../../../PostCollection';

export const DELETE_POST_MUTATION = graphql(`
  mutation DeletePost($data: DeletePostInput) {
    deletePost(params: $data) {
      success
    }
  }
`);

const POST_DELETE_MENU_ITEM_POST_FRAGMENT = graphql(`
  fragment postDeleteMenuItemPostFragment on Post {
    _id
  }
`);

const POST_DELETE_MENU_ITEM_GROUP_FRAGMENT = graphql(`
  fragment postDeleteMenuItemGroupFragment on Group {
    _id
    machineName
  }
`);

interface Props {
  post: DocumentType<typeof POST_DELETE_MENU_ITEM_POST_FRAGMENT>;
  group: DocumentType<typeof POST_DELETE_MENU_ITEM_GROUP_FRAGMENT>;
  onClose: () => void;
  serviceMachineName?: string | null;
  location?: 'myHub' | 'adminHub' | null;
}

export const PostDeleteMenuItem = ({
  post,
  group,
  onClose,
  serviceMachineName,
  location,
}: Props) => {
  const { _id: groupId, machineName } = group;

  let groupIds: string[] | null = null;
  let userId: string | null | undefined = null;

  if (location === 'adminHub') {
    const { groupFilterIds } = use(ServiceAdminMultiGroupContext);
    groupIds = groupFilterIds;
  }

  if (location === 'myHub') {
    const { groupFilterIds } = use(MultiGroupContext);
    groupIds = groupFilterIds;
    const { currentUserId } = use(CurrentUserContext);
    userId = currentUserId;
  }

  const [deletePost, { loading }] = useMutation(DELETE_POST_MUTATION, {
    completionMessage: 'Post deleted',
    onCompleted: onClose,
    optimisticResponse: {
      deletePost: {
        __typename: 'DeletePostResult',
        success: true,
      },
    },
    update(cache, { data }) {
      if (!data?.deletePost?.success) {
        return;
      }

      if (!machineName) {
        return;
      }

      cache.updateQuery(
        {
          query: POST_COLLECTION_QUERY,
          variables: {
            tagIds: null,
            machineName,
            first: null,
            cursor: null,
          },
        },
        (data) => {
          if (!data?.group?.postsConnection) {
            return data;
          }

          const { edges } = data.group.postsConnection;
          const newEdges = edges.filter(({ node }) => node._id !== post._id);

          return {
            ...data,
            group: {
              ...data.group,
              postsConnection: {
                ...data.group.postsConnection,
                edges: newEdges,
              },
            },
          };
        },
      );

      if (!groupId) {
        return;
      }

      cache.updateQuery(
        {
          query: PINNED_POSTS_COLLECTION_QUERY,
          variables: {
            groupId,
          },
        },
        (data) => {
          if (!data?.group?.pinnedPostsConnection) {
            return data;
          }

          const { edges } = data.group.pinnedPostsConnection;
          const newEdges = edges.filter(({ node }) => node._id !== post._id);

          return {
            ...data,
            group: {
              ...data.group,
              pinnedPostsConnection: {
                ...data.group.pinnedPostsConnection,
                edges: newEdges,
              },
            },
          };
        },
      );

      if (!serviceMachineName) {
        return;
      }

      cache.updateQuery(
        {
          query: SERVICE_GROUPS_POSTS_QUERY,
          variables: {
            machineName: serviceMachineName,
            first: POST_LIMIT,
            after: '',
            groupIds,
          },
        },
        (data) => {
          if (!data?.service?.groupPostsConnection) {
            return data;
          }

          const { edges } = data.service.groupPostsConnection;
          const newEdges = edges.filter(({ node }) => node._id !== post._id);

          return {
            ...data,
            service: {
              ...data.service,
              groupPostsConnection: {
                ...data.service.groupPostsConnection,
                edges: newEdges,
              },
            },
          };
        },
      );

      cache.updateQuery(
        {
          query: SERVICE_GROUPS_PINNED_POSTS_QUERY,
          variables: {
            machineName: serviceMachineName,
            first: POST_LIMIT,
            after: '',
            groupIds,
          },
        },
        (data) => {
          if (!data?.service?.pinnedDashboardPostsConnection) {
            return data;
          }

          const { edges } = data.service.pinnedDashboardPostsConnection;
          const newEdges = edges.filter(({ node }) => node._id !== post._id);

          return {
            ...data,
            service: {
              ...data.service,
              pinnedDashboardPostsConnection: {
                ...data.service.pinnedDashboardPostsConnection,
                edges: newEdges,
              },
            },
          };
        },
      );

      if (!userId) {
        return;
      }

      cache.updateQuery(
        {
          query: USERS_GROUPS_POSTS_QUERY,
          variables: {
            userId,
            first: POST_LIMIT,
            after: '',
            groupIds,
          },
        },
        (data) => {
          if (!data?.user?.groupPostsConnection) {
            return data;
          }

          const { edges: postEdges } = data.user.groupPostsConnection;
          const newPostEdges = postEdges.filter(
            ({ node }) => node._id !== post._id,
          );

          const { edges: pinnedPostEdges } =
            data.user.pinnedDashboardPostsConnection;
          const newPinnedPostEdges = pinnedPostEdges.filter(
            ({ node }) => node._id !== post._id,
          );

          return {
            ...data,
            user: {
              ...data.user,
              groupPostsConnection: {
                ...data.user.groupPostsConnection,
                edges: newPostEdges,
              },
              pinnedDashboardPostsConnection: {
                ...data.user.pinnedDashboardPostsConnection,
                edges: newPinnedPostEdges,
              },
            },
          };
        },
      );
    },
  });

  return (
    <MenuItem
      disabled={loading}
      onClick={() => {
        deletePost({
          variables: {
            data: {
              post: post._id,
            },
          },
        });
      }}
    >
      <ListItemIcon>
        <DeleteOutlined />
      </ListItemIcon>
      <ListItemText primary="Delete" />
    </MenuItem>
  );
};
